import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  Theme,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";
import Field from "components/Field";
import SelectApplication from "components/SelectApplication";
import { FORM_ERROR } from "final-form";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import { useMemo } from "react";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import translateError from "utils/translateError";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

type GiveAccessToApplicationFormProps = {
  userId: string;
  currentApplicationIds: string[];
  onCancel?: () => void;
  onApplicationUserAccessGiven: () => void;
};

type ApplicationFormValues = {
  applicationId: string;
};

const { Form } = withTypes<ApplicationFormValues>();

const GiveAccessToApplicationForm = ({
  userId,
  currentApplicationIds,
  onCancel,
  onApplicationUserAccessGiven,
}: GiveAccessToApplicationFormProps) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { applicationUserService } = useGlobalContext();

  return useMemo(() => {
    const init: ApplicationFormValues = {
      applicationId: "",
    };

    const onSubmit = async (values: ApplicationFormValues) => {
      try {
        await applicationUserService.enableAccessForApplication(
          userId,
          values.applicationId
        );
        onApplicationUserAccessGiven();
      } catch (err: any) {
        return { [FORM_ERROR]: translateError(err, t) };
      }
    };

    return (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Grid container spacing={1}>
                <Field name="applicationId" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectApplication
                          {...input}
                          label={t({ id: "applicationLabel" })}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                            required: true,
                          }}
                          required
                          allowGrantingUserAccess={true}
                          excludeApplicationIds={currentApplicationIds}
                        />

                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button disabled={submitting} onClick={onCancel}>
                {t({ id: "cancelButton" })}
              </Button>

              <Button type="submit" color="primary" disabled={submitting}>
                {t({ id: "applicationUserGiveAccessButtonLabel" })}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    );
  }, [
    applicationUserService,
    currentApplicationIds,
    onApplicationUserAccessGiven,
    onCancel,
    styles.feedback,
    t,
    userId,
  ]);
};

export default GiveAccessToApplicationForm;
